import {
  Query,
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from "@tanstack/react-query";
import { type AxiosInstance, AxiosResponse } from "axios";
import { useMemo, useEffect } from "react";
import { PAY_PERIOD_TEMPLATE_START_DAY_OF_WEEK } from "@/business/constants";
import { useGetBusiness } from "@/business/query";
import { useApi } from "@/core/api";
import { ApiError } from "@/core/api/types";
import {
  applyCreatedAiScriptRunsToPayPeriods,
  createdAiScriptRunsByPeriodKey,
  type CreatedAiScriptRunByPeriodStart,
} from "@/payroll/ai-script/utils";
import {
  PAYROLL_RUN_POLL_INTERVAL,
  PAYROLL_RUN_STATUS,
} from "@/payroll/constants";
import {
  IPayPeriod,
  IPayPeriodDetail,
  EnrichedPayPeriod,
  IPayrollSheetApprovalAction,
  IPayrollSheetApprovalStatus,
  SheetApprovalType,
} from "@/payroll/payroll.d";
import {
  enrichPayPeriod,
  mergePayPeriodsWithCalculatedGaps,
} from "@/payroll/utils/period-helpers";

export const payPeriodsKey = (businessId?: string | null) =>
  ["pay-periods", businessId] as const;

/**
 * The run id is part of the key on purpose. The period SHOW payload carries
 * sheet approval state derived from the LATEST COMPLETED run, and a rerun can
 * retract approvals server-side where the regenerated numbers changed. Keying on
 * the run means a new run is simply a different cache entry that React Query
 * fetches on its own — no invalidation to remember, and no way to leave sign-offs
 * on screen that the backend has already withdrawn.
 *
 * Callers that only want the period's payroll runs pass no run id and share one
 * entry, exactly as before.
 */
/** Every period-detail entry, whatever run it is keyed to. */
export const payPeriodDetailPrefixKey = () => ["pay-period-detail"] as const;

export const payPeriodDetailPeriodKey = (
  businessId?: string | null,
  periodId?: string | null,
) => ["pay-period-detail", businessId, periodId] as const;

export const payPeriodDetailKey = (
  businessId?: string | null,
  periodId?: string | null,
  runId?: string | null,
) => ["pay-period-detail", businessId, periodId, runId ?? null] as const;

export const useGetPayPeriods = (
  businessId?: string | null,
  options?: {
    refetchInterval?:
      | number
      | false
      | ((query: Query<EnrichedPayPeriod[]>) => number | false);
  },
): UseQueryResult<EnrichedPayPeriod[]> => {
  const api = useApi();
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: payPeriodsKey(businessId),
    queryFn: (): Promise<EnrichedPayPeriod[]> =>
      api
        .get(`/businesses/${businessId}/pay_periods`)
        .then((response) =>
          applyCreatedAiScriptRunsToPayPeriods(
            response.data.map(enrichPayPeriod),
            queryClient.getQueryData<CreatedAiScriptRunByPeriodStart>(
              createdAiScriptRunsByPeriodKey(businessId),
            ),
          ),
        )
        .catch((error) => {
          // Handle 404 as "no periods exist yet"
          if (error instanceof ApiError && error.status === 404) {
            return [];
          }
          throw error;
        }),
    enabled: !!businessId,
    refetchInterval: options?.refetchInterval,
  });
};

export const hasActivePayrollRun = (period?: IPayPeriodDetail) =>
  Boolean(
    period?.payroll_runs.some(
      (run) =>
        run.status === PAYROLL_RUN_STATUS.pending ||
        run.status === PAYROLL_RUN_STATUS.processing,
    ),
  );

export const useGetPayPeriodDetail = (
  businessId?: string | null,
  periodId?: string | null,
  options?: {
    enabled?: boolean;
    /**
     * The completed AI run this period's workbook is showing. Pass it wherever
     * `approval_status` is read — see payPeriodDetailKey for why it belongs in
     * the key rather than in a refetch someone has to remember to trigger.
     */
    runId?: string | null;
  },
): UseQueryResult<IPayPeriodDetail> => {
  const api = useApi();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: payPeriodDetailKey(businessId, periodId, options?.runId),
    queryFn: (): Promise<IPayPeriodDetail> =>
      api
        .get(`/businesses/${businessId}/pay_periods/${periodId}`)
        .then((response) => response.data),
    enabled: !!businessId && !!periodId && (options?.enabled ?? true),
    // Follow a running payroll to completion, then stop on its own.
    refetchInterval: (query) =>
      hasActivePayrollRun(query.state.data) ? PAYROLL_RUN_POLL_INTERVAL : false,
    retry: (failureCount, error) => {
      // Don't retry on 404 - if period doesn't exist, retrying won't help
      if (error instanceof ApiError && error.status === 404) return false;
      return failureCount < 3;
    },
  });

  // Update the periods list cache when detail data changes
  useEffect(() => {
    if (!query.data) return;

    // Manually update the corresponding period in the periods list cache
    queryClient.setQueryData<EnrichedPayPeriod[]>(
      payPeriodsKey(businessId),
      (oldPeriods) => {
        if (!oldPeriods) return oldPeriods;

        return oldPeriods.map((period) => {
          if (period.id === periodId) {
            // Compute latest run defensively (don't assume API ordering)
            const runs = query.data.payroll_runs;
            const latestRun =
              runs.length > 0
                ? runs.reduce((latest, run) =>
                    run.created_at > latest.created_at ? run : latest,
                  )
                : null;
            return {
              ...period,
              runs_count: runs.length,
              latest_run_at: latestRun?.created_at ?? null,
              latest_run_status: latestRun?.status ?? null,
            };
          }
          return period;
        });
      },
    );
  }, [query.data, businessId, periodId, queryClient]);

  return query;
};

export interface CreatePayrollSheetApprovalActionParams {
  scope?: "group";
  sheet_id?: string;
  access_key_id: string;
  originating_ai_script_run_id: string;
  approval_type: SheetApprovalType;
  enabled: boolean;
  reason?: string;
}

interface CreatePayrollSheetApprovalActionResponse {
  approval_action: IPayrollSheetApprovalAction;
  approval_status: IPayrollSheetApprovalStatus;
}

export const postPayrollSheetApprovalAction = (
  api: AxiosInstance,
  businessId: string,
  periodId: string,
  params: CreatePayrollSheetApprovalActionParams,
): Promise<CreatePayrollSheetApprovalActionResponse> =>
  api
    .post(
      `/businesses/${businessId}/pay_periods/${periodId}/payroll_sheet_approval_actions`,
      params,
    )
    .then((response) => response.data);

/**
 * Folds a fresh approval status into a cached period detail. The create endpoint
 * returns the recomputed status for the whole period, so the toggle the user just
 * flipped and every sibling sheet sharing its access key settle in one write —
 * no refetch, no half-updated strip.
 */
export const applyPayrollSheetApprovalStatus = (
  period: IPayPeriodDetail | undefined,
  approvalStatus: IPayrollSheetApprovalStatus,
): IPayPeriodDetail | undefined =>
  period ? { ...period, approval_status: approvalStatus } : period;

export const useCreatePayrollSheetApprovalAction = (
  businessId?: string | null,
  periodId?: string | null,
) => {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: CreatePayrollSheetApprovalActionParams) =>
      postPayrollSheetApprovalAction(api, businessId!, periodId!, params),
    // Only the detail cache: approval status lives on the show endpoint alone
    // (see IPayPeriodDetail), so the periods index has nothing to update. The
    // run the action was posted AGAINST is what identifies the entry to write —
    // if a rerun landed mid-flight, this writes to the superseded entry and the
    // live one refetches, rather than painting a stale status over fresh data.
    onSuccess: ({ approval_status }, variables) =>
      queryClient.setQueryData<IPayPeriodDetail>(
        payPeriodDetailKey(
          businessId,
          periodId,
          variables.originating_ai_script_run_id,
        ),
        (period) => applyPayrollSheetApprovalStatus(period, approval_status),
      ),
    // A refused action means our picture is stale in some way the client can't
    // work out: the run was superseded, the key deactivated, or this user's
    // approval access revoked. Re-read both rather than guess which.
    onError: (_error, variables) => {
      void queryClient.invalidateQueries({
        queryKey: payPeriodsKey(businessId),
      });
      void queryClient.invalidateQueries({
        queryKey: payPeriodDetailKey(
          businessId,
          periodId,
          variables.originating_ai_script_run_id,
        ),
      });
    },
  });
};

interface CreatePayPeriodParams {
  start_date: string;
  end_date: string;
}

export const PAYROLL_RUN_TYPE = {
  icm: "icm",
  legacyScript: "legacy_script",
} as const;

export type PayrollRunType =
  (typeof PAYROLL_RUN_TYPE)[keyof typeof PAYROLL_RUN_TYPE];

interface TriggerPayrollRunParams {
  run_type: PayrollRunType;
  icm_plan_id?: string;
}

export const useCreatePayPeriod = (businessId: string | null) => {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (
      params: CreatePayPeriodParams,
    ): Promise<
      AxiosResponse<{
        pay_period: IPayPeriod;
        run: { id: string; status: string; created_at: string };
      }>
    > => api.post(`/businesses/${businessId}/pay_periods`, params),
    onSuccess: () => {
      // Invalidate to refetch with fresh data from server
      void queryClient.invalidateQueries({
        queryKey: payPeriodsKey(businessId),
      });
    },
  });
};

export const useTriggerPayrollRun = (
  businessId: string | null,
  periodId: string | null,
) => {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (
      params?: TriggerPayrollRunParams,
    ): Promise<
      AxiosResponse<{ run: { id: string; status: string; created_at: string } }>
    > =>
      api.post(
        `/businesses/${businessId}/pay_periods/${periodId}/payroll_runs`,
        params,
      ),
    onSuccess: () => {
      // Invalidate both detail and periods list to ensure UI updates
      void queryClient.invalidateQueries({
        queryKey: payPeriodDetailKey(businessId, periodId),
      });
      void queryClient.invalidateQueries({
        queryKey: payPeriodsKey(businessId),
      });
    },
  });
};

export const useGetEnrichedPayPeriods = (businessId?: string | null) => {
  const { data: business, isLoading: isBusinessLoading } =
    useGetBusiness(businessId);
  const { data: payPeriods = [], isLoading: isPayPeriodsLoading } =
    useGetPayPeriods(businessId);
  const template = business?.current_pay_period_template;
  const enrichedPeriods = useMemo(() => {
    const resolvedTemplate = template || {
      cadence: "weekly",
      start_day_of_week: PAY_PERIOD_TEMPLATE_START_DAY_OF_WEEK.monday,
      period_start_reference_date: "",
    };
    return mergePayPeriodsWithCalculatedGaps(payPeriods, resolvedTemplate);
  }, [payPeriods, template]);

  return {
    enrichedPeriods,
    isLoading: isBusinessLoading || isPayPeriodsLoading,
  };
};
